import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../services/api';
import Pagination from '../components/shared/Pagination';
import { TableSkeleton } from '../components/shared/LoaderSkeleton';

// Premium RTO Agents Index - Architectural Harmony with InvoiceShow
export default function RtoAgentsIndex() {
    const location = useLocation();
    const [agents, setAgents] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({ 
        total_pages: 1, 
        total_count: 0, 
        current_page: 1,
        per_page: 25 
    });

    useEffect(() => {
        loadAgents();
    }, [location, currentPage, searchTerm]);

    const loadAgents = async () => {
        try {
            setLoading(true);
            const params: any = { 
                page: currentPage,
                q: searchTerm
            };
            const response = await api.get('/rto_agents', { params });
            if (response.success) {
                const agentsData = response.data.data || response.data || [];
                setAgents(agentsData);
                if (response.meta) {
                    setPagination({
                        total_pages: response.meta.total_pages || 1,
                        total_count: response.meta.total_count || 0,
                        current_page: response.meta.current_page || 1,
                        per_page: response.meta.per_page || 25
                    });
                }
            }
        } catch (error) {
            console.error('Error loading RTO agents:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this RTO agent?')) return;

        try {
            await api.delete(`/rto_agents/${id}`);
            loadAgents();
        } catch (error) {
            alert('Error deleting agent. It may be in use.');
        }
    };

    if (loading && agents.length === 0) return <TableSkeleton />;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-slate-50/30 min-h-screen">
            {/* Header / Actions - Styled like InvoiceShow */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        <Link to="/" className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-100 transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                        </Link>
                        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                            RTO <span className="text-indigo-600">Facilitators</span>
                        </h1>
                        <span className="px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-100">
                            {pagination.total_count} Agents
                        </span>
                    </div>
                    <p className="text-sm text-slate-500 ml-10">
                        Managing jurisdiction <span className="font-semibold text-slate-700">service liaison officers</span>
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <Link
                        to="/rto_agents/new"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-semibold hover:bg-black transition-all shadow-sm shadow-slate-200"
                    >
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4" /></svg>
                        Register Agent
                    </Link>
                </div>
            </div>

            {/* Metrics Row - Segmented Layout */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-slate-100 uppercase tracking-widest mb-8">
                <div className="p-5 flex-1 group">
                    <h3 className="text-[10px] font-bold text-slate-400 mb-1">Service Density</h3>
                    <p className="text-xl font-black text-slate-900 font-mono">{pagination.total_count}</p>
                    <p className="text-[9px] font-bold text-slate-400 mt-1">Identified Personnel</p>
                </div>
                <div className="p-5 flex-1 group">
                    <h3 className="text-[10px] font-bold text-slate-400 mb-1">Active Mandates</h3>
                    <p className="text-xl font-black text-indigo-600 font-mono">{agents.filter(a => a.is_active).length}</p>
                    <p className="text-[9px] font-bold text-slate-400 mt-1">Operational status</p>
                </div>
                <div className="p-5 flex-1 group">
                    <h3 className="text-[10px] font-bold text-slate-400 mb-1">Zones Covered</h3>
                    <p className="text-xl font-black text-emerald-600 font-mono">{new Set(agents.map(a => a.service_area)).size}</p>
                    <p className="text-[9px] font-bold text-slate-400 mt-1">Operating Jurisdictions</p>
                </div>
            </div>

            {/* Search Bar */}
            <div className="mb-6">
                <div className="relative group max-w-md">
                    <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search agents by name or zone..."
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        className="w-full pl-11 pr-4 py-3 bg-white rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none text-sm font-medium transition-all shadow-sm"
                    />
                </div>
            </div>

            {/* Data Table - Refined Style */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden mb-8 transition-all hover:shadow-md">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-sm">
                        <thead>
                            <tr className="bg-slate-50/50 border-b border-slate-100 uppercase tracking-widest text-[10px] font-black text-slate-400">
                                <th className="px-6 py-4">Agent Identity</th>
                                <th className="px-6 py-4">Assigned Jurisdiction</th>
                                <th className="px-6 py-4">Communication & Fees</th>
                                <th className="px-6 py-4 text-center">Status</th>
                                <th className="px-6 py-4 text-right">Operations</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {agents.map((agent: any) => (
                                <tr key={agent.id} className="hover:bg-slate-50/50 transition-colors group">
                                    <td className="px-6 py-4">
                                        <Link to={`/rto_agents/${agent.id}`} className="font-bold text-slate-900 hover:text-indigo-600 transition-colors">
                                            {agent.agent_name}
                                        </Link>
                                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-tight mt-0.5">UID: RTO-{agent.id.toString().padStart(3, '0')}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="font-semibold text-slate-700">{agent.service_area || 'Universal Area'}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col gap-0.5">
                                            <div className="flex items-center gap-1.5 text-slate-600 font-medium text-[11px]">
                                                <svg className="w-3 h-3 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                                                {agent.phone || 'No Contact'}
                                            </div>
                                            <div className="flex items-center gap-1.5 text-indigo-600 font-black tracking-tighter text-[10px] uppercase">
                                                <svg className="w-3 h-3 text-indigo-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                                Comm: {agent.commission_rate ? `${agent.commission_rate}%` : 'Standard'}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-tighter ${
                                            agent.is_active ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-rose-50 text-rose-700 border border-rose-100'
                                        }`}>
                                            {agent.is_active ? 'Operational' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex items-center justify-end gap-1 underline-none">
                                            <Link
                                                to={`/rto_agents/${agent.id}/edit`}
                                                className="p-2 text-slate-300 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                            </Link>
                                            <button
                                                onClick={() => handleDelete(agent.id)}
                                                className="p-2 text-slate-300 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {agents.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-slate-400 italic text-sm">
                                        No service facilitators identified in the registry.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination Alignment */}
            <div className="flex justify-end pt-2">
                <Pagination
                    currentPage={pagination.current_page}
                    totalPages={pagination.total_pages}
                    totalCount={pagination.total_count}
                    perPage={pagination.per_page}
                    onPageChange={handlePageChange}
                />
            </div>
        </div>
    );
}
